import  React, { useEffect, useState } from 'react';
import { Linking, ActivityIndicator, Modal, Platform } from "react-native";
import type { AccountProps, FocusPositionProps, ItemOrderProps, ItemProps, MyPlayerProps, PlayerAddressProps, TransactionProps } from '../types';
import { Icons } from '../Components';
import { CheckoutApi, CheckoutHelpers } from './api';
import ItemSummaryCard from './components/ItemSummaryCard';
import OrderSummaryCard from './components/OrderSummaryCard';
import AccountSummaryCard from './components/AccountSummaryCard';
import AddressManager from '../Wallet/components/AddressManager';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import PayPalOrderCapture from './components/PayPalOrderCapture';
import { usePlayerLocation } from '../LocationTracker/LocationStatus';
import LocationTracker from '../LocationTracker';
import { WalletApi } from '../Wallet/api';
import ShippingSummaryCard from './components/ShippingSummaryCard';
import { Button, Text, View } from '../Components/Themed';
import { useColors } from '../constants/useColors';
import WebView from 'react-native-webview';
import DisclaimerBox from './components/DisclaimerBox';

/*
Modal Management
- Confirm Order -> 
1) When the order comes back launch the confirm or redirect modal
2) If redirect.  HandleRedirect does the following
    -Launches the completing modal (Web view)
3) When the order comes back with a new status of not redirect
    - Close the completing modal
*/


type CheckoutProps = {
    item_order:ItemOrderProps,
    account_id?:string,
    height?:number,
    onFocusPosition?:(pos:FocusPositionProps) => void,
    onRequestNewAccount:() => void,
    onCompleteOrder:(item_order:ItemOrderProps, transaction:TransactionProps) => void,
    onCancel:() => void
}
const Checkout = ({ item_order, height, account_id, onCancel, onCompleteOrder, onFocusPosition, onRequestNewAccount }:CheckoutProps) => {
    const Colors = useColors();
    const [ loading, setLoading ] = useState(false);
    const [ disclaimer, setDisclaimer ] = useState(false);
    const [ show_disclaim, setShowDisclaim ] = useState(false);
    const [ redirect_visible, setRedirectVisible ] = useState(false);
    const [ completing, setCompleting ] = useState(false);
    const [ error, setError ] = useState<string|undefined>();
    const [ show_addresses, setShowAddresses ] = useState<{
        visible:boolean,
        address_type?: 'billing'|'shipping'
    }>({
        visible:false
    });
    const [ cvv, setCVV ] = useState<string>();
    const [ show_location, setShowLocation ] = useState(false);
    const [ checkout_data, setCheckoutData ] = useState<{
        loaded:boolean,
        player?:MyPlayerProps,
        accounts:AccountProps[],
        player_addresses:PlayerAddressProps[],
        selected_account?:AccountProps
        billing_address?:PlayerAddressProps,
        shipping_address?:PlayerAddressProps,
        items:ItemProps[]
    }>({
        loaded:false,
        items:[],
        player_addresses: [],
        accounts:[]
    })
    const [ checkout, setCheckout ] = useState<{
        ip_item_order?:ItemOrderProps,
        ip_transaction?:TransactionProps
    }>({});
    const [ draft_order, setDraftOrder ] = useState<ItemOrderProps>();
    const { loaded, player, accounts, items, player_addresses , selected_account, billing_address, shipping_address } = checkout_data;
    const { ip_item_order, ip_transaction } = checkout;

    const { permission, location, getLocation, checkPermissions } = usePlayerLocation({ player_id: player?.player_id });
    
    const shipping_address_required = CheckoutHelpers.isShippingRequired(items);
    const billing_address_required = CheckoutHelpers.isBillingAddressRequired(items, selected_account);

    //const isLocationRequired = CheckoutHelpers.isLocationRequired(items);

    const will_redirect = selected_account?.account_type == 'ach' && selected_account?.ach_detail?.ach_type == 'ibf' ? true : false

    useEffect(() => {
        CheckoutApi.setEnvironment();
        WalletApi.setEnvironment();
        if(!item_order){ return setDraftOrder(undefined) }
        getDetailsFromServer(item_order, account_id);
    },[item_order])

    useEffect(() => {
        if(!permission.checked){ return }
        if(permission.permission != 'granted'){ return setShowLocation(true) }
        if(location.location && show_location){ return setShowLocation(false) }
    },[permission])

    /**
     * Get all necessary checkout data from the server
     * @param item_order 
     * @param account_id 
     */
    const getDetailsFromServer = async(item_order:ItemOrderProps, account_id?:string) => {
        checkPermissions({ auto_on_granted:true, request_if_possible:true });
        const player = await CheckoutApi.getMyDetails();
        const order_items = await CheckoutApi.getItemsByIds(item_order.items.map(i => i.item_id));
        const accounts = await CheckoutApi.getMyAccounts();
        let eligible_accounts = CheckoutHelpers.filterMyPurchaseAccounts(accounts, order_items)
        const addresses = await CheckoutApi.getMyAddresses();
        let a = eligible_accounts.find(a => a.account_id == account_id)
        if(!a){
            a = eligible_accounts.find(a => a.default_account);
        }
        let ba:PlayerAddressProps | undefined = addresses.find(addy => addy.player_address_id == a?.default_billing_address_id);
        
        if(a){ item_order = CheckoutHelpers.genItemOrder(item_order, order_items, a) }
        
        setDraftOrder(item_order);

        setCheckoutData({
            loaded:true,
            player,
            selected_account:a,
            player_addresses: addresses,
            billing_address: ba,
            items: order_items,
            accounts: accounts
        })
    }

    const order_errors = !draft_order ? [] : CheckoutHelpers.isOrderValid(draft_order, items, selected_account, cvv, billing_address, shipping_address, location.location, location.coordinates);
    const handleConfirm = async() => {
        if(!draft_order || order_errors.length > 0){ return }
        if(error){ return onCancel()}
        if(!disclaimer){ return alert('Must agree to terms before continuing') }
        setLoading(true);
        const draft_transaction = CheckoutHelpers.genTransactionFromItemOrder(draft_order, items, selected_account?.account_id, cvv);
        try {
            const resp = await CheckoutApi.createOrder({ ...draft_order, region:location.region, location: location.coordinates }, { ...draft_transaction, billing_address_id: billing_address?.player_address_id });
            setCheckout({
                ip_item_order: resp.item_order,
                ip_transaction: resp.transaction
            });
            setRedirectVisible(true)
        } catch (e) {
            console.log(e)
            setError('Unable to complete your order. Please try again or reach out to support if the issue persists')
            setCheckout({
                ip_item_order: { ...draft_order, status: 'failed' },
                ip_transaction: { ...draft_transaction, status: 'failed' }
            })
            setRedirectVisible(true);
            setLoading(false);
        }
    }

    const handleCancel = async() => {
        if(!ip_transaction){ return onCancel() }
        await CheckoutApi.cancelTransaction(ip_transaction.transaction_id);
        setCheckout({});
        onCancel();
    }

    const checkTransactionStatus = async() => {
        if(!ip_transaction){ return }
        const resp = await CheckoutApi.getTransactionById(ip_transaction.transaction_id);
        setCheckout({ ...checkout, ip_transaction:resp.transaction })
        if(resp.transaction.status == 'redirect'){
            setTimeout(() => {
                checkTransactionStatus()
            }, 2000);
        } else {
            setCompleting(false);
        }

    }

    const handleChangeAccount = async(account:AccountProps) => {
        let new_address = player_addresses.find(pa => pa.player_address_id == account.default_billing_address_id);
        setCheckoutData({
            ...checkout_data,
            selected_account: account,
            billing_address: new_address ? new_address : billing_address
            
        })
        if(draft_order){
            let new_draft_order = CheckoutHelpers.genItemOrder(draft_order, items, account)
            setDraftOrder(new_draft_order)
        }
    }

    const handleChangeBillingAddress = async(address:PlayerAddressProps, address_type?:'shipping'|'billing') => {
        if(selected_account && address_type == 'billing'){ 
            await CheckoutApi.updateAccountDefaultAddress(selected_account.account_id, address.player_address_id) 
        }
        if(address_type == 'billing'){
            setCheckoutData({ ...checkout_data, billing_address: address, player_addresses: player_addresses.filter(pa => pa.player_address_id != address.player_address_id).concat(address) })
        } else {
            setCheckoutData({ ...checkout_data, shipping_address: address, player_addresses: player_addresses.filter(pa => pa.player_address_id != address.player_address_id).concat(address) })
            if(draft_order){ setDraftOrder({ ...draft_order, shipping_address_id: address.player_address_id }) }
        }
        setShowAddresses({ visible:false })
    }

    const handleRedirect = async() => {
        if(!ip_transaction?.redirect_url){ return }
        if(completing){ return }
        setCompleting(true);
        if(Platform.OS == 'web'){
            Linking.openURL(ip_transaction.redirect_url)
        }
        //Native we will use the webview component
        setTimeout(() => {
            checkTransactionStatus()
        }, 5000);
    }

    if(!draft_order || !player || !loaded){
        return (
            <View style={{ flex:1, height, padding:20 }}>
                <Text size={16} theme='h1' textAlign='center'>LOADING YOUR CART</Text>
                <ActivityIndicator size='large' color={Colors.text.h1} style={{ alignSelf:'center', marginTop:30 }} />
            </View>
        )
    }

    return (
        
        <View style={{ flex:1, height }}>
            <View style={{ flex:1 }}>
                <View nativeID='items' style={{ borderBottomWidth:1, borderBottomColor:Colors.borders.light, padding:10}}>
                    <ItemSummaryCard
                        items={items}
                    />
                </View>
                <View nativeID='order_summary' style={{ borderBottomWidth:1, borderBottomColor:Colors.borders.light, padding:10}}>
                    <OrderSummaryCard
                        item_order={draft_order}
                    />
                </View>
                {shipping_address_required ?
                <View nativeID='shipping_summary' style={{ borderBottomWidth:1, borderBottomColor:Colors.borders.light, padding:10}}>
                    <ShippingSummaryCard
                        player_address={shipping_address}
                        onChangeAddress={() => setShowAddresses({ visible: true, address_type: 'shipping' })}
                    />
                </View>
                :<></>}
                <View nativeID='account_summary' style={{ borderBottomWidth:1, borderBottomColor:Colors.borders.light, padding:10}}>
                    <AccountSummaryCard
                        accounts={accounts}
                        items={items}
                        onFocusPosition={onFocusPosition}
                        selected_account={selected_account}
                        player_address={billing_address}
                        billing_address_required={billing_address_required}
                        cvv={cvv}
                        onRequestNewAccount={onRequestNewAccount}
                        onCVVChange={(new_cvv) => setCVV(new_cvv)}
                        onAccountsUpdate={(accts) => {
                            setCheckoutData({
                                ...checkout_data,
                                selected_account: accts.find(a => a.account_id == selected_account?.account_id),
                                accounts: accounts.filter(a => !accts.find(na => na.account_id == a.account_id)).concat(accts)
                            })
                        }}
                        onChangeAddress={() => setShowAddresses({
                            visible:true,
                            address_type:'billing'
                        })}
                        onSelectAccount={(account) => handleChangeAccount(account)}
                    />
                </View>
            </View>
            {order_errors.length > 0 ?
            <View float style={{ margin:10, borderRadius:4,  padding:10 }}>
                <Text style={{ marginBottom:5 }} size={14} theme='h2'>Please fix the following before continuing</Text>
                {order_errors.map(e => {
                    return (
                        <Text style={{ padding:4 }} size={12} color={Colors.text.warning} theme='h1'>{e}</Text>
                    )
                })}
            </View>
            :<></>}
            {error?
            <View style={{ padding:10 }}>
                <Text size={14} color={Colors.text.warning} theme='h1' textAlign='center'>{error}</Text>
            </View>  
            :<></>}
            <PayPalScriptProvider  
              options={{ 
                  clientId: CheckoutApi.getPayPalClientId(),
              }}> 
                <View style={{ padding:20 }}>
                {!disclaimer ?
                <View nativeID='action_row' style={{ flexDirection:'row', alignItems:'center' }}>
                    <Button
                        style={{ flex:1, marginRight:5, opacity:loading?0.5:1 }}
                        title='CANCEL'
                        disabled={loading}
                        type='error'
                        onPress={() => handleCancel()}
                    />
                    <Button
                        style={{ flex:3, marginLeft:5, opacity:loading?0.5:1 }}
                        title='AGREE TO TERMS'
                        type='action'
                        onPress={() => setShowDisclaim(true)}
                    />
                </View>
                :!ip_transaction ?
                <View nativeID='action_row' style={{ flexDirection:'row', alignItems:'center' }}>
                    <Button
                        style={{ flex:1, marginRight:5, opacity:loading?0.5:1 }}
                        title='CANCEL'
                        disabled={loading}
                        type='error'
                        onPress={() => handleCancel()}
                    />
                    {selected_account?.account_type == 'paypal' && Platform.OS == 'web' ?
                    <PayPalOrderCapture
                        draft_item_order={draft_order}
                        paypal_account={selected_account}
                        region={location.region}
                        coordinates={location.coordinates}
                        button_height={48}
                        items={items}
                        order_valid={order_errors.length == 0 ? true : false}
                        onOrderUpdate={(item_order, transaction) => setCheckout({ ip_item_order: item_order, ip_transaction: transaction })}
                        onCancel={() => console.log('DONE!')}
                    />
                    : selected_account?.account_type == 'paypal' && Platform.OS != 'web' ?
                    <Button
                        loading={loading || location.loading}
                        disabled={loading || order_errors.length > 0}
                        style={{ flex:1, alignItems:'center', padding:10 }}
                        backgroundColor={Colors.absolutes.company.paypal}
                        onPress={() => handleConfirm()}
                    >
                        <Icons.PayPalIcon size={90}/>
                    </Button>
                    :
                    <Button
                        style={{ flex:3, opacity:loading || order_errors.length > 0 || error ? 0.5 : 1 }}
                        loading={loading || location.loading}
                        disabled={loading || order_errors.length > 0}
                        title={location.loading ?'VERIFYING LOCATION': will_redirect ? 'CONFIRM & CONTINUE' : 'SUBMIT PURCHASE'}
                        title_color={Colors.text.white}
                        backgroundColor={ will_redirect ? Colors.absolutes.brand.electric : Colors.absolutes.utility.success }
                        onPress={() => handleConfirm()}
                    />
                    }
                </View>
                : <></>}
                </View>
            </PayPalScriptProvider>
            {redirect_visible ?
            <View type='blur' style={{ flex:1, position:'absolute', top:0, left:0, right:0, bottom:0, justifyContent:'flex-end' }}>
                {ip_item_order && ip_transaction ?
                    <View float style={{ borderTopRightRadius:22, borderTopLeftRadius:22 }}>
                        {ip_transaction.status == 'posted' ?
                        <View style={{ padding:10, paddingTop:30, paddingBottom:30, justifyContent:'center', alignItems:'center' }}>
                            <Icons.TransactionSuccessIcon  size={150} />
                            <View style={{ padding:20, borderRadius:8, borderWidth:1, borderColor:Colors.text.success, marginTop:20 }}>
                                <Text size={20} color={Colors.text.success} theme='h1' textAlign='center'>Order Successful</Text>
                                <Text style={{ marginTop:10 }} size={16} theme='description' textAlign='center'>Your order was successfully processed. An email confirmation has been sent. Please select close below to continue.</Text>
                            </View>
                        </View>
                        : ip_transaction.status == 'failed' ?
                        <View style={{ padding:10, paddingTop:30, paddingBottom:30, justifyContent:'center', alignItems:'center' }}>
                            <Icons.TransactionErrorIcon  size={150} />
                            <View style={{ padding:20, borderRadius:8, borderWidth:1, borderColor:Colors.text.error, marginTop:20 }}>
                                <Text size={20} color={Colors.text.error} theme='h1' textAlign='center'>Order Failed</Text>
                                <Text style={{ marginTop:10 }} size={16} theme='description' textAlign='center'>Sorry, we were unable to process your order at this time.  Please try again or reach out to support for help.</Text>
                            </View>
                        </View>
                        :ip_transaction.status == 'cancelled' ?
                        <View style={{ padding:10, paddingTop:30, paddingBottom:30, justifyContent:'center', alignItems:'center' }}>
                            <Icons.TransactionErrorIcon  size={150} />
                            <View style={{ padding:20, borderRadius:8, borderWidth:1, borderColor:Colors.text.error, marginTop:20 }}>
                                <Text size={20} color={Colors.text.error} theme='h1' textAlign='center'>Order Cancelled</Text>
                                <Text style={{ marginTop:10 }} size={16} theme='description' textAlign='center'>Your order was cancelled.  Please try again.</Text>
                            </View>
                        </View>
                        : ip_transaction.status == 'redirect' && selected_account?.account_type == 'ach' ?
                        <View style={{ padding:10, paddingTop:30, paddingBottom:30, justifyContent:'center', alignItems:'center' }}>
                            <Icons.IBTFLowIcon  size={300} />
                            <View style={{ padding:20, borderRadius:8, borderWidth:1, borderColor:Colors.borders.light, marginTop:20 }}>
                                <Text size={20} theme='h1' textAlign='center'>Finish With Plaid</Text>
                                <Text style={{ marginTop:10 }} size={16} theme='description' textAlign='center'>Select Complete Order below to complete the transaction through Plaid</Text>
                            </View>
                        </View>
                        : ip_transaction.status == 'redirect' && selected_account?.account_type == 'paypal' ?
                        <View style={{ padding:10, paddingTop:30, paddingBottom:30, justifyContent:'center', alignItems:'center' }}>
                            <Icons.PayPalIcon  size={250} />
                            <View style={{ padding:20, borderRadius:8, borderWidth:1, borderColor:Colors.borders.light, marginTop:20 }}>
                                <Text size={20} theme='h1' textAlign='center'>Finish With PayPal</Text>
                                <Text style={{ marginTop:10 }} size={16} theme='description' textAlign='center'>Select Complete Order below to complete the transaction through PayPal</Text>
                            </View>
                        </View>
                        :<></>
                        }
                        <View nativeID='action_row' style={{ flexDirection:'row', padding:20 }}>
                            {ip_transaction.status == 'redirect' || ip_transaction.status == 'pending' ?
                            <Button
                                style={{ flex:1, marginRight:5, opacity:1 }}
                                title='CANCEL'
                                //disabled={loading}
                                type='error'
                                onPress={() => handleCancel()}
                            />
                            :
                            <Button
                                style={{ flex:1, opacity:1 }}
                                title='CLOSE'
                                type='error'
                                onPress={() => onCompleteOrder(ip_item_order, ip_transaction)}
                            />
                            }
                            {ip_transaction.status == 'redirect' ?
                            <Button
                                style={{ flex:3, opacity:completing?0.5:1 }}
                                loading={completing}
                                disabled={completing}
                                title='COMPLETE ORDER'
                                type='success'
                                onPress={() => handleRedirect()}
                            />
                            :<></>}
                        </View>
                </View>
            :<></>}
            </View>
            :<></>}
            {Platform.OS != 'web' && completing ? 
            <View type='blur' style={{ flex:1, position:'absolute', top:0, left:0, right:0, bottom:0, padding:20, paddingTop:50, paddingBottom:50 }}>
                <View float style={{flex:1}}>
                    {completing && ip_transaction && ip_transaction.redirect_url ?
                    <WebView
                        style={{ flex:1 }}
                        originWhitelist={['*']}
                        source={{ uri:ip_transaction.redirect_url }}
                    />
                :<></>}
                </View>
            </View>
            :<></>}
            <Modal
                visible={show_addresses.visible}
                transparent
                animationType='slide'
                style={{flex:1}}
            >
                <View type='blur' style={{ flex:1 }}>
                    <AddressManager
                        player={player}
                        onFocusPosition={onFocusPosition}
                        player_addresses={player_addresses}
                        location={location}
                        onCancel={() => setShowAddresses({ visible:false })}
                        onRemoveAddress={(addy) => {
                            let selected_addy = billing_address && billing_address.player_address_id == addy.player_address_id ? undefined : billing_address
                            if(show_addresses.address_type == 'billing'){
                                setCheckoutData({ ...checkout_data, billing_address: selected_addy, player_addresses: player_addresses.filter(pa => pa.player_address_id != addy.player_address_id) })
                            } else {
                                setCheckoutData({ ...checkout_data, shipping_address: selected_addy, player_addresses: player_addresses.filter(pa => pa.player_address_id != addy.player_address_id) })
                            }
                        }}
                        onAddAddress={(addy) => handleChangeBillingAddress(addy, show_addresses.address_type)}
                        onSelect={(addy) => handleChangeBillingAddress(addy, show_addresses.address_type)}
                    />
                </View>
            </Modal>
            
            {show_location ?
            <View style={{ position:'absolute', top:0, bottom:0, left:0, right:0, justifyContent:'flex-end' }}>
                    <LocationTracker 
                        location={location}
                        permission={permission}
                        onGetLocation={getLocation}
                        onPromptForPermission={() => checkPermissions()}
                        onClose={() => setShowLocation(false)}
                    />
            </View>
            :<></>}
            {show_disclaim ?
            <View style={{ position:'absolute', top:0, left:0, right:0, bottom:0, justifyContent:'flex-end', padding:10 }}>
                <DisclaimerBox 
                    checked={disclaimer}
                    onCheck={(checked) => setDisclaimer(checked)}
                    onClose={() => setShowDisclaim(false)}
                />
            </View>
            :<></>}
        </View>
    )
}


export default Checkout